import React from 'react';
import { Zap, X, Check } from 'lucide-react';
import { 
  BatchItemInput, 
  BatchItemSimulationResult 
} from '../../lib/batchTaxSimulatorEngine';

export interface InvoiceComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeItem: BatchItemInput | null;
  activeResult: BatchItemSimulationResult | null;
  onUpdateItem: (updates: Partial<BatchItemInput>) => void;
  formatBRL: (val: number) => string;
  formatPct: (val: number) => string;
}

export default function InvoiceComparisonModal({
  isOpen,
  onClose,
  activeItem,
  activeResult,
  onUpdateItem,
  formatBRL,
  formatPct,
}: InvoiceComparisonModalProps) {
  if (!isOpen || !activeItem || !activeResult) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full border border-slate-200 shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Cabeçalho do Modal */}
        <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center">
              <Zap className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-900">
                Simulador de Nota Fiscal (Entrada vs Saída)
              </h3>
              <p className="text-[11px] text-slate-500">
                {activeItem.name} ({activeItem.sku || 'Sem SKU'})
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo com as duas colunas: Entrada vs Saída */}
        <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-6 max-h-[70vh] overflow-y-auto">
          
          {/* 📥 COLUNA 1: NOTA FISCAL DE ENTRADA (COMPRA / FORNECEDOR) */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-3.5">
            <div className="flex items-center justify-between border-b border-slate-200 pb-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                <span>📥 Nota de Entrada</span>
              </h4>
              <span className="text-[10px] font-bold text-slate-500">Compra / Fornecedor</span>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Custo Unitário na NF (R$):
              </label>
              <input
                type="number"
                step="0.10"
                min="0"
                value={activeItem.costPrice}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  onUpdateItem({ costPrice: isNaN(val) ? 0 : val });
                }}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Regime do Fornecedor:
              </label>
              <select
                value={activeItem.supplierTaxRegime || 'regime_geral'}
                onChange={(e) => {
                  onUpdateItem({ supplierTaxRegime: e.target.value as any });
                }}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer"
              >
                <option value="regime_geral">Regime Geral (Crédito Integral CBS/IBS)</option>
                <option value="simples_nacional">Simples Nacional (Crédito Parcial 4%)</option>
                <option value="isento_zero">Isento / Cesta Zero (Crédito R$ 0)</option>
              </select>
            </div>

            {/* Resumo da Entrada com Alíquotas e Valores CBS/IBS Separados */}
            <div className="pt-2 border-t border-slate-200 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-700">
                <span className="font-medium">Alíquota Entrada:</span>
                <span className="font-bold font-mono text-emerald-900 bg-emerald-100 px-2 py-0.5 rounded border border-emerald-200">
                  {activeResult.inputCreditPct.toFixed(1)}%
                </span>
              </div>
              <div className="text-[10px] text-slate-500 text-right font-mono">
                CBS: {activeResult.inputCreditCbsRatePct.toFixed(1)}% | IBS: {activeResult.inputCreditIbsRatePct.toFixed(1)}%
              </div>
              <div className="flex justify-between items-center text-slate-700 pt-1">
                <span className="font-medium">Crédito Fiscal na Compra:</span>
                <strong className="text-emerald-700 font-bold">+{formatBRL(activeResult.inputCreditVal)}</strong>
              </div>
              <div className="text-[10px] text-slate-500 text-right font-mono">
                CBS: +{formatBRL(activeResult.inputCreditCbsVal)} | IBS: +{formatBRL(activeResult.inputCreditIbsVal)}
              </div>
              <div className="flex justify-between items-center text-slate-900 font-bold pt-1 border-t border-slate-200">
                <span>Custo Líquido Estoque:</span>
                <span className="font-black">{formatBRL(activeResult.netStockCost)}</span>
              </div>
            </div>
          </div>

          {/* 📤 COLUNA 2: NOTA FISCAL DE SAÍDA (VENDA / CLIENTE) */}
          <div className="p-4 bg-indigo-50/30 border border-indigo-200 rounded-2xl space-y-3.5">
            <div className="flex items-center justify-between border-b border-indigo-200 pb-2">
              <h4 className="text-xs font-black uppercase tracking-wider text-indigo-950 flex items-center gap-1.5">
                <span>📤 Nota de Saída</span>
              </h4>
              <span className="text-[10px] font-bold text-indigo-700">Venda / Faturamento</span>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Tipo e Código Fiscal:
              </label>
              <div className="flex gap-2">
                <select
                  value={activeItem.itemType || (activeResult.fiscalType === 'NBS' ? 'service' : 'product')}
                  onChange={(e) => {
                    onUpdateItem({ itemType: e.target.value as any });
                  }}
                  className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                >
                  <option value="product">Produto (NCM)</option>
                  <option value="service">Serviço (NBS)</option>
                </select>
                <input
                  type="text"
                  value={activeItem.nbs || activeItem.ncm || activeItem.fiscalCode || ''}
                  placeholder={activeResult.fiscalType === 'NBS' ? '1.2501.10.00' : '1006.30.21'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (activeItem.itemType === 'service') {
                      onUpdateItem({ nbs: val, fiscalCode: val });
                    } else {
                      onUpdateItem({ ncm: val, fiscalCode: val });
                    }
                  }}
                  className="flex-1 px-3 py-1.5 bg-white border border-slate-300 rounded-xl text-xs font-mono font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1">
                Benefício / Redução Fiscal (Reforma):
              </label>
              <select
                value={activeItem.taxReduction || activeResult.reductionCategory}
                onChange={(e) => {
                  onUpdateItem({ taxReduction: e.target.value as any });
                }}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
              >
                <option value="zero">Alíquota Zero (Redução Integral)</option>
                <option value="reducao_60">Redução de 60% (Saúde, Educação, Medicamentos)</option>
                <option value="reducao_30">Redução de 30% (Profissões Intelectuais Regulamentadas)</option>
                <option value="padrao">Alíquota Padrão (Sem Redução)</option>
                <option value="seletivo">Imposto Seletivo (+IS)</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] font-bold text-slate-700">
                  Preço Simulado na NF de Saída (R$):
                </label>
                <button
                  type="button"
                  onClick={() => {
                    onUpdateItem({ simulatedSalePrice: activeResult.suggestedSalePrice });
                  }}
                  className="text-[10px] font-bold text-indigo-700 hover:text-indigo-950 underline cursor-pointer"
                >
                  Usar Sugerido ({formatBRL(activeResult.suggestedSalePrice)})
                </button>
              </div>
              <input
                type="number"
                step="0.50"
                min="0"
                value={activeItem.simulatedSalePrice ?? activeResult.suggestedSalePrice}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  onUpdateItem({ simulatedSalePrice: isNaN(val) ? 0 : val });
                }}
                className="w-full px-3 py-2 bg-white border border-indigo-300 rounded-xl text-sm font-black text-indigo-950 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Resumo da Saída com Alíquotas e Valores CBS/IBS Separados */}
            <div className="pt-2 border-t border-indigo-200 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-700">
                <span className="font-medium">Alíquota Saída:</span>
                <span className="font-bold font-mono text-indigo-900 bg-indigo-100 px-2 py-0.5 rounded border border-indigo-200">
                  {activeResult.totalTaxRatePct.toFixed(1)}%
                </span>
              </div>
              <div className="text-[10px] text-slate-500 text-right font-mono">
                CBS: {activeResult.cbsRatePct.toFixed(1)}% | IBS: {activeResult.ibsRatePct.toFixed(1)}%
              </div>
              <div className="flex justify-between items-center text-slate-700 pt-1">
                <span className="font-medium">Débito Fiscal na Venda:</span>
                <strong className="text-rose-700 font-bold">{formatBRL(activeResult.taxOnSaleVal)}</strong>
              </div>
              <div className="text-[10px] text-slate-500 text-right font-mono">
                CBS: {formatBRL(activeResult.taxOnSaleCbsVal)} | IBS: {formatBRL(activeResult.taxOnSaleIbsVal)}
              </div>
              <div className="flex justify-between items-center text-slate-900 font-bold pt-1 border-t border-indigo-200">
                <span>Novo Preço Sugerido:</span>
                <span className="font-black text-emerald-900">{formatBRL(activeResult.suggestedSalePrice)}</span>
              </div>
            </div>

          </div>

        </div>

        {/* Barra de Confronto Tributário (Split Payment) */}
        <div className="p-5 bg-slate-900 text-white flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full sm:w-auto text-xs">
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-bold block">1. Débitos Saída:</span>
              <strong className="text-sm font-black text-rose-400">{formatBRL(activeResult.taxOnSaleVal)}</strong>
              <span className="text-[9px] text-slate-400 block font-mono">CBS {formatBRL(activeResult.taxOnSaleCbsVal)} | IBS {formatBRL(activeResult.taxOnSaleIbsVal)}</span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-bold block">2. (-) Créditos Entrada:</span>
              <strong className="text-sm font-black text-emerald-400">-{formatBRL(activeResult.inputCreditVal)}</strong>
              <span className="text-[9px] text-slate-400 block font-mono">CBS -{formatBRL(activeResult.inputCreditCbsVal)} | IBS -{formatBRL(activeResult.inputCreditIbsVal)}</span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-bold block">3. (=) A Recolher (Split):</span>
              <strong className="text-sm font-black text-amber-400">{formatBRL(activeResult.netTaxPayableVal)}</strong>
              <span className="text-[9px] text-slate-400 block font-mono">CBS {formatBRL(activeResult.cbsToPayVal)} | IBS {formatBRL(activeResult.ibsToPayVal)}</span>
            </div>
            <div>
              <span className="text-slate-400 text-[10px] uppercase font-bold block">Margem Líquida Real:</span>
              <strong className="text-sm font-black text-emerald-400">{formatPct(activeResult.projectedMarginPct)}</strong>
              <span className="text-[9px] text-slate-400 block font-mono">Lucro: {formatBRL(activeResult.projectedMarginVal)}</span>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-full sm:w-auto px-5 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-1.5 shadow-md cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>Concluir Simulação</span>
          </button>
        </div>

      </div>
    </div>
  );
}
